import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { Controller, useForm } from 'react-hook-form'
import Button from '~/components/Button'
import TextInput from '~/components/TextInput'
import { getResourceAuthorization, getResourceAuthorizationIsLoading } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import { useRelationMutation } from '@verzekeringapp-mono/shared/src/hooks/use_relation_mutation'
import { useNavigate } from 'react-router-dom'
import { parse, format } from 'date-fns'
import { getPendingRelationMutations } from '@verzekeringapp-mono/shared/src/state/selectors/relation_mutations'
import useUser from '@verzekeringapp-mono/shared/src/hooks/use_user'
import LoadingIndicator from '~/components/LoadingIndicator'
import { IAutoCompleteAddress } from '@verzekeringapp-mono/shared/src/models/address_auto_complete'
import { useAutocompleteAddress } from '@verzekeringapp-mono/shared/src/hooks/use_autocomplete_address'
import REGEX from '@verzekeringapp-mono/shared/src/constants/regex'
import { useIbanValidator } from '@verzekeringapp-mono/shared/src/hooks/use_iban_validator'
import DatePicker from 'react-datepicker'
import { FORMATTED_DUTCH_IBAN_LENGTH, formatDutchIban, isValidDutchIban, normalizeDutchIban } from '@verzekeringapp-mono/shared/src/utils/iban'

interface FormData {
  phoneNumber: string
  accountNumber: string
  accountName: string
  dateOfBirth: string
  city: string
  zipCode: string
  street: string
  houseNumber: number
  houseNumberAddition: string
}

const ManageRelationScreen: React.FC = () => {
  const { requestRelationMutation } = useRelationMutation()
  const navigate = useNavigate()
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const resourceAuthorizationIsLoading = useAppSelector(getResourceAuthorizationIsLoading)
  const pendingMutations = useAppSelector(getPendingRelationMutations)
  const hasPendingMutations = pendingMutations?.hasActiveRelationChange ?? false
  const {
    ibanValidationCheckDone,
    ibanValidated,
    ibanSuggestion,
    clearIbanSuggestion,
    validateIbanWithDebounce
  } = useIbanValidator()
  const { validateAddressWithDebounce, isLoading } = useAutocompleteAddress()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isIbanSuggestionValidationPending, setIsIbanSuggestionValidationPending] = useState(false)

  const { deleteUser } = useUser()

  const dateOfBirth = useMemo(() => {
    if (!resourceAuthorization?.relationInfo?.dateOfBirth) return ''

    const parsedDate = parse(resourceAuthorization?.relationInfo?.dateOfBirth, 'dd-MM-yyyy', new Date())

    return format(parsedDate, 'yyyy-MM-dd')
  }, [resourceAuthorization?.relationInfo?.dateOfBirth])

  const confirmDeleteAccount = useCallback(() => {
    deleteUser().then(() => {
      navigate('/home', { replace: true })
      alert('Je account is succesvol verwijderd.')
    }).catch((err) => {
      alert(err.message || 'Er is een fout opgetreden bij het verwijderen van je account. Probeer het later opnieuw.')
    })
  }, [deleteUser, navigate])

  const deleteAccount = useCallback(() => {
    const confirmed = confirm('Weet je zeker dat je je account wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.')

    if (confirmed) {
      confirmDeleteAccount()
    }
  }, [confirmDeleteAccount])

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid, isDirty },
    trigger
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      phoneNumber: resourceAuthorization?.relationInfo?.phoneNumber,
      accountNumber: formatDutchIban(resourceAuthorization?.relationInfo?.accountNumber ?? ''),
      accountName: resourceAuthorization?.relationInfo?.accountName ?? '',
      dateOfBirth: dateOfBirth,
      city: resourceAuthorization?.relationInfo?.city ?? '',
      zipCode: resourceAuthorization?.relationInfo?.zipCode ?? '',
      street: resourceAuthorization?.relationInfo?.street ?? '',
      houseNumber: resourceAuthorization?.relationInfo?.houseNumber,
      houseNumberAddition: resourceAuthorization?.relationInfo?.houseNumberAddition ?? ''
    }
  })
  const accountNumber = watch('accountNumber')
  const accountName = watch('accountName')

  const formatPhoneNumber = (phoneNumber: string): string =>
    phoneNumber.replace(/\D/g, '')

  const setAddressFormFields = useCallback((address?: IAutoCompleteAddress) => {
    setValue('street', address?.street ?? '')
    setValue('city', address?.city ?? '')

    trigger(['street', 'city'])
  }, [setValue])

  const autoCompleteAddress = useCallback(() => {
    const zipCode = watch('zipCode')
    const houseNumber = watch('houseNumber')

    validateAddressWithDebounce(
      zipCode,
      houseNumber,
      setAddressFormFields,
      setAddressFormFields
    )
  }, [watch('zipCode'), watch('houseNumber'), validateAddressWithDebounce, setAddressFormFields])

  const validateIban = useCallback((nextAccountNumber = accountNumber, nextAccountName = accountName) => {
    setIsIbanSuggestionValidationPending(false)
    validateIbanWithDebounce(nextAccountNumber, nextAccountName)
  }, [accountName, accountNumber, validateIbanWithDebounce])

  useEffect(() => {
    if (ibanValidationCheckDone) {
      setIsIbanSuggestionValidationPending(false)
    }
  }, [ibanValidationCheckDone])

  const showIbanValidationWarning = ibanValidationCheckDone && !ibanValidated && !ibanSuggestion

  const applyIbanSuggestion = useCallback(() => {
    if (!ibanSuggestion) {
      return
    }

    const accountNumber = watch('accountNumber')

    setValue('accountName', ibanSuggestion, { shouldDirty: true, shouldValidate: true })
    clearIbanSuggestion()
    setIsIbanSuggestionValidationPending(true)
    validateIbanWithDebounce(accountNumber, ibanSuggestion)
  }, [clearIbanSuggestion, ibanSuggestion, setValue, validateIbanWithDebounce, watch])

  if (!resourceAuthorization) {
    return null
  }

  const formatZipCode = (zipCode: string): string => {
    if (zipCode.length === 6) {
      return `${zipCode.slice(0, 4)} ${zipCode.slice(4, 6)}`
    }

    return zipCode
  }

  return (
    <ScreenWrapper>
      <div className='flex w-full flex-col rounded border border-border-subtle bg-surface-card p-8'>
        <form
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onSubmit={handleSubmit(async data => {
            const refKey = resourceAuthorization.relationInfo?.agentNumber
            const relationNumber = resourceAuthorization.relationInfo?.relationNumber

            if (!refKey || !relationNumber) {
              alert('Er is een fout opgetreden. Probeer het later opnieuw.')

              return
            }
            setIsSubmitting(true)
            try {
              const result = await requestRelationMutation(refKey, relationNumber, {
                accountNumber: normalizeDutchIban(data.accountNumber),
                accountName: data.accountName,
                city: data.city,
                dateOfBirth: data.dateOfBirth,
                houseNumber: data.houseNumber,
                houseNumberAddition: data.houseNumberAddition,
                street: data.street,
                phoneNumber: formatPhoneNumber(data.phoneNumber ?? ''),
                zipCode: formatZipCode(data.zipCode),
                ibanValidated: ibanValidated
              })

              if (result) {
                window.alert(
                  'Je wijziging is ingediend' +
                  '\n\n' +
                  'De wijziging wordt zo spoedig mogelijk door je adviseur in behandeling genomen. Zodra de wijziging is verwerkt, worden de gewijzigde gegevens zichtbaar in de app.'
                )

                navigate('/mijn-profiel')
              }
            } catch (err) {
              // do nothing
            } finally {
              setIsSubmitting(false)
            }
          })}
          className='md:w-1/2 w-full'
        >
          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='phone'
                placeholder='Telefoonnummer'
                label='Telefoonnummer'
                maxLength={10}
              />
            )}
            disabled={hasPendingMutations}
            name='phoneNumber'
            control={control}
            rules={{
              pattern: {
                value: REGEX.phoneNumber,
                message: 'Ongeldig telefoonnummer'
              }
            }}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='text'
                placeholder='Naam rekeninghouder'
                label='Naam rekeninghouder'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e)
                  validateIban(accountNumber, e.target.value)
                }}
                showActionIcon={ibanValidated}
                actionIcon={'check-circle'}
              />
            )}
            name='accountName'
            control={control}
            disabled={hasPendingMutations}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='text'
                placeholder='IBAN'
                style={{ textTransform: 'uppercase' }}
                label='IBAN'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const formattedIban = formatDutchIban(e.target.value)

                  field.onChange(formattedIban)
                  validateIban(formattedIban)
                }}
                onBlur={(e) => {
                  field.onChange(formatDutchIban(e.target.value))
                  field.onBlur()
                }}
                maxLength={FORMATTED_DUTCH_IBAN_LENGTH}
                showActionIcon={ibanValidated}
                actionIcon={'check-circle'}
                tip={ibanValidated ? 'Je naam en Iban zijn gecontroleerd' : showIbanValidationWarning ? 'IBAN-controle mislukt. Controleer de naam en het rekeningnummer. Of neem contact op met je adviseur.' : undefined}
                tipContainerClassName={ibanValidated ? 'mb-2 rounded bg-status-success-subtle p-4 text-status-success-foreground' : showIbanValidationWarning ? 'mb-2 rounded bg-status-warning-subtle p-4 text-status-warning-foreground' : undefined}
                validationText={fieldState.error?.message}
                showValidationText={true}
                validationTextContainerClassName='mb-2 rounded bg-status-danger-subtle p-4'
              />
            )}
            name='accountNumber'
            control={control}
            rules={{
              validate: value => isValidDutchIban(value) || 'Ongeldige nederlandse IBAN'
            }}
            disabled={hasPendingMutations}
          />

          {ibanSuggestion && (
            <button
              type='button'
              className='mb-4 text-left text-sm font-semibold text-text-link underline'
              onClick={applyIbanSuggestion}
              disabled={hasPendingMutations}
            >
              Bedoel je misschien: {ibanSuggestion}?
            </button>
          )}

          <div className='mb-6 flex flex-col'>
            <label className='label'>
              Geboortedatum
            </label>

            <Controller
              render={({ field }) => (
                <DatePicker
                  locale='nl'
                  id='dateOfBirth'
                  wrapperClassName='w-full'
                  selected={field.value ? parse(field.value, 'yyyy-MM-dd', new Date()) : null}
                  onChange={(date: Date | null) => {
                    const formattedDate = date ? format(date, 'yyyy-MM-dd') : ''

                    field.onChange(formattedDate)
                  }}
                  minDate={ new Date(1900, 0, 1) }
                  maxDate= { new Date(new Date().setFullYear(new Date().getFullYear() - 15))}
                  dateFormat='dd-MM-yyyy'
                  placeholderText='dd-mm-jjjj'
                  disabled={hasPendingMutations}
                  className='textfield mt-4'
                />
              )}
              name='dateOfBirth'
              control={control}
              rules={{ required: true }}
            />
          </div>

          <div className='relative'>
            <Controller
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  isValid={!fieldState.invalid}
                  type='text'
                  placeholder='Postcode'
                  label='Postcode'
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e)
                    autoCompleteAddress()
                  }}
                />
              )}
              name='zipCode'
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: REGEX.zipCode,
                  message: 'Ongeldige postcode'
                }
              }}
              disabled={hasPendingMutations}
            />

            { isLoading &&
            <div className='absolute right-3 top-1/2 translate-y-[10%] '>
              <LoadingIndicator/>
            </div>
            }

          </div>

          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='text'
                placeholder='Huisnummer'
                label='Huisnummer'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e)
                  autoCompleteAddress()
                }}
              />
            )}
            name='houseNumber'
            control={control}
            rules={{
              required: 'Huisnummer is verplicht',
              pattern: {
                value: REGEX.houseNumber,
                message: 'Voer een geldig huisnummer in'
              },
              max: {
                value: 99999,
                message: 'Huisnummer te hoog'
              }
            }}
            disabled={hasPendingMutations}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='text'
                placeholder='Huisnummer toevoeging'
                label='Huisnummer toevoeging'
              />
            )}
            name='houseNumberAddition'
            control={control}
            rules={{
              pattern: {
                value: REGEX.houseNumberAddition,
                message: 'Voer een geldige toevoeging in'
              }
            }}
            disabled={hasPendingMutations}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='text'
                placeholder='Straat'
                label='Straat'
              />
            )}
            name='street'
            control={control}
            rules={{
              required: 'Straat is verplicht',
              pattern: {
                value: REGEX.street,
                message: 'Voer een geldige straatnaam in'
              }
            }}
            disabled={hasPendingMutations}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isValid={!fieldState.invalid}
                type='text'
                placeholder='Plaats'
                label='Plaats'
              />
            )}
            name='city'
            control={control}
            rules={{
              required: 'Plaats is verplicht',
              pattern: {
                value: REGEX.city,
                message: 'Voer een geldige plaats in'
              }
            }}
            disabled={hasPendingMutations}
          />

          {
            hasPendingMutations && (
              <p className='mb-4 mt-8 rounded bg-status-warning-subtle p-4 text-sm text-status-warning-foreground'>
                Je vorige wijzigingsverzoek moet nog worden verwerkt door je adviseur.
              </p>
            )
          }

          {
            !hasPendingMutations && (
              <p className='mb-4 mt-8 rounded bg-surface-info p-4 text-sm text-text-secondary'>
                Opmerking: je gegevens worden niet direct gewijzigd. Deze worden
                eerst gecontroleerd door je adviseur.
              </p>
            )
          }

          <Button
            className='w-full mb-3'
            type='submit'
            isLoading={resourceAuthorizationIsLoading || isSubmitting}
            disabled={!isValid || !isDirty || hasPendingMutations || (ibanValidationCheckDone && !ibanValidated) || Boolean(ibanSuggestion) || isIbanSuggestionValidationPending}
          >
            {hasPendingMutations ? 'Wijziging in behandeling' : 'Wijziging doorgeven'}
          </Button>

          <Button
            className='w-full mt-4'
            variant='danger'
            onClick={deleteAccount}
            type='button'
          >
            Account verwijderen
          </Button>
        </form>
      </div>
    </ScreenWrapper>
  )
}

export default ManageRelationScreen
